import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Empty, Button } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { convertUTCToLocalTime } from '@helpers/dateHelpers'
import moment from 'moment'
import {
  fetchInspectionAvailability,
  createInspectionAvailability,
  updateInspectionAvailability,
  deleteInspectionAvailability,
} from '@app/store/reducers/order/orderSlice'
import {
  selectInspectionAvailabilityData,
  selectInspectionAvailabilityStatus,
} from '@app/store/reducers/order/orderSelectors'
import STATUS from '@app/common/constants/reducer_statuses'
import Loader from '@app/common/layout/components/Loader'
import AvailabilityItem from './AvailabilityItem'

const InspectionAvailabilityModal = ({ visible, onCancel, orderData }) => {
  const dispatch = useDispatch()
  const availabilityData = useSelector(selectInspectionAvailabilityData)
  const availabilityStatus = useSelector(selectInspectionAvailabilityStatus)

  const [localAvailability, setLocalAvailability] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [savingItemId, setSavingItemId] = useState(null)
  const [timezone, setTimezone] = useState(null)

  useEffect(() => {
    if (visible && orderData?.id) {
      dispatch(fetchInspectionAvailability(orderData.id))
    }
  }, [visible, orderData?.id, dispatch])

  // Initialize local state when data is fetched
  useEffect(() => {
    if (availabilityData?.inspection_availability) {
      const timezone = availabilityData.timezone
      // Convert UTC times from backend to the specified timezone for display
      const formatted = availabilityData.inspection_availability.map((item) => ({
        id: item.id,
        start: convertUTCToLocalTime(item.start, timezone),
        end: convertUTCToLocalTime(item.end, timezone),
        is_active_for_inspection: item.is_active_for_inspection,
      }))
      setLocalAvailability(formatted)
      setTimezone(timezone)
    } else if (availabilityStatus === STATUS.SUCCEEDED && !availabilityData?.inspection_availability) {
      setLocalAvailability([])
      setTimezone(null)
    }
  }, [availabilityData, availabilityStatus])

  const handleAdd = () => {
    // Create new availability in the property's timezone
    const now = timezone ? convertUTCToLocalTime(Date.now(), timezone) : moment()
    const newItem = {
      id: `new-${Date.now()}`,
      start: now.clone().startOf('hour').add(1, 'hour'),
      end: now.clone().startOf('hour').add(3, 'hour'),
      is_active_for_inspection: true,
    }
    setLocalAvailability([...localAvailability, newItem])
    setEditingId(newItem.id)
  }

  const handleEdit = (id) => {
    setEditingId(id)
  }

  const handleRemove = async (id) => {
    // Check if it's a new item (not saved yet)
    const isNewItem = id.toString().startsWith('new-')

    if (isNewItem) {
      // Just remove from local state if it's new
      setLocalAvailability(localAvailability.filter((item) => item.id !== id))
      if (editingId === id) {
        setEditingId(null)
      }
    } else {
      // Call DELETE endpoint for existing items
      setSavingItemId(id)
      try {
        await dispatch(
          deleteInspectionAvailability({
            orderId: orderData.id,
            id,
          }),
        ).unwrap()

        // Refresh data after delete
        await dispatch(fetchInspectionAvailability(orderData.id))

        if (editingId === id) {
          setEditingId(null)
        }
      } catch (error) {
        console.error('Failed to delete inspection availability:', error)
      } finally {
        setSavingItemId(null)
      }
    }
  }

  const handleItemSave = async (updatedItem) => {
    const isNewItem = updatedItem.id.toString().startsWith('new-')
    setSavingItemId(updatedItem.id)

    try {
      // Convert times to UTC before saving
      const startUTC = updatedItem.start.clone().utc()
      const endUTC = updatedItem.end.clone().utc()

      if (isNewItem) {
        // Create new item
        await dispatch(
          createInspectionAvailability({
            orderId: orderData.id,
            start: startUTC.valueOf(),
            end: endUTC.valueOf(),
            is_active_for_inspection: updatedItem.is_active_for_inspection,
          }),
        ).unwrap()
      } else {
        // Update existing item
        await dispatch(
          updateInspectionAvailability({
            orderId: orderData.id,
            id: updatedItem.id,
            start: startUTC.valueOf(),
            end: endUTC.valueOf(),
            is_active_for_inspection: updatedItem.is_active_for_inspection,
          }),
        ).unwrap()
      }

      // Refresh data after save
      await dispatch(fetchInspectionAvailability(orderData.id))
      setEditingId(null)
    } catch (error) {
      console.error('Failed to save inspection availability:', error)
    } finally {
      setSavingItemId(null)
    }
  }

  const handleItemCancel = () => {
    setEditingId(null)
  }

  const handleModalCancel = () => {
    // Reset editing state
    setEditingId(null)
    onCancel()
  }

  const renderAvailabilityContent = () => {
    if (availabilityStatus === STATUS.LOADING) {
      return <Loader />
    }

    if (availabilityStatus === STATUS.SUCCEEDED && localAvailability.length === 0) {
      return (
        <Empty description="No inspection availability set" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Availability
          </Button>
        </Empty>
      )
    }

    // Sort by start date/time (ascending)
    const sortedAvailability = [...localAvailability].sort((a, b) => {
      return a.start.valueOf() - b.start.valueOf()
    })

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!!savingItemId}>
            Add Availability
          </Button>
          {timezone && (
            <div style={{ color: '#666', fontSize: 14 }}>
              Timezone: <strong>{timezone}</strong>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sortedAvailability.map((item) => (
            <AvailabilityItem
              key={item.id}
              item={item}
              isEditing={editingId === item.id}
              onEdit={handleEdit}
              onSave={handleItemSave}
              onCancel={handleItemCancel}
              onDelete={handleRemove}
              isSaving={savingItemId === item.id}
              timezone={timezone}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <Modal
      title="Inspection Availability"
      visible={visible}
      onCancel={handleModalCancel}
      footer={[
        <button key="close" className="btn btn-secondary" onClick={handleModalCancel}>
          Close
        </button>,
      ]}
      width={800}
    >
      {renderAvailabilityContent()}
    </Modal>
  )
}

export default InspectionAvailabilityModal
